import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { TenantDetail } from "../request/request";

// ====================== View Partner Modal ======================
export interface PartnerViewModalProps {
  isOpen: boolean;
  isLoading: boolean;
  partner: TenantDetail | null;
  onClose: () => void;
}

export const PartnerViewModal: React.FC<PartnerViewModalProps> = ({
  isOpen,
  isLoading,
  partner,
  onClose,
}) => {
  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Partner Details"
      size="lg"
    >
      {isLoading ? (
        <div className="space-y-6">
          <SkeletonBlock h={20} w="35%" className="mb-3" />
          <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 12 }).map((_, idx) => (
              <SkeletonText key={idx} lines={2} lineHeight={14} />
            ))}
          </div>
        </div>
      ) : (
        partner && (
          <div className="grid grid-cols-3 gap-4">
            <GenericInput
              label="ID"
              value={String(partner.id ?? "")}
              readOnly
            />
            <GenericInput
              label="Name"
              value={partner.Name ?? ""}
              readOnly
            />
            <GenericInput
              label="Display Name"
              value={partner.nameDisplay ?? ""}
              readOnly
            />
            <GenericInput
              label="Domain"
              value={partner.Domain ?? ""}
              readOnly
            />
            <GenericInput
              label="Chargebee Customer ID"
              value={partner.chargebeeCustomerId ?? ""}
              readOnly
            />
            <GenericInput
              label="Org ID"
              value={partner.OrgID ?? ""}
              readOnly
            />
            <GenericInput
              label="Jira Organization ID"
              value={partner.jiraOrganizationId ?? ""}
              readOnly
            />
            <GenericInput
              label="Tenant Type"
              value={partner.TenantType?.name ?? ""}
              readOnly
            />
            <GenericInput
              label="Has MFA"
              value={partner.hasMFA ? "Yes" : "No"}
              readOnly
            />
            <GenericInput
              label="Can Access Alerts"
              value={partner.canAccessAlerts ? "Yes" : "No"}
              readOnly
            />
            <GenericInput
              label="Created At"
              value={partner.createdAt ? new Date(partner.createdAt).toLocaleString() : ""}
              readOnly
            />
            <GenericInput
              label="Updated At"
              value={partner.updatedAt ? new Date(partner.updatedAt).toLocaleString() : ""}
              readOnly
            />
          </div>
        )
      )}
    </GenericModal>
  );
};

// ====================== Edit Partner Modal ======================
export interface PartnerEditModalProps {
  isOpen: boolean;
  isLoading: boolean;
  partner: TenantDetail | null;
  onClose: () => void;
  onSave: () => void;
  onFieldChange: (field: string, value: string | boolean) => void;
}

export const PartnerEditModal: React.FC<PartnerEditModalProps> = ({
  isOpen,
  isLoading,
  partner,
  onClose,
  onSave,
  onFieldChange,
}) => {
  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Partner"
      size="lg"
    >
      {isLoading ? (
        <div className="space-y-6">
          <SkeletonBlock h={20} w="30%" className="mb-3" />
          <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 12 }).map((_, idx) => (
              <SkeletonText key={idx} lines={2} lineHeight={14} />
            ))}
          </div>
        </div>
      ) : partner ? (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <GenericInput
              label="ID"
              value={String(partner.id ?? "")}
              readOnly
            />
            <GenericInput
              label="Name"
              value={partner.Name ?? ""}
              onChange={(value) => onFieldChange("Name", value)}
            />
            <GenericInput
              label="Display Name"
              value={partner.nameDisplay ?? ""}
              onChange={(value) => onFieldChange("nameDisplay", value)}
            />
            <GenericInput
              label="Domain"
              value={partner.Domain ?? ""}
              onChange={(value) => onFieldChange("Domain", value)}
            />
            <GenericInput
              label="Chargebee Customer ID"
              value={partner.chargebeeCustomerId ?? ""}
              onChange={(value) => onFieldChange("chargebeeCustomerId", value)}
            />
            <GenericInput
              label="Org ID"
              value={partner.OrgID ?? ""}
              onChange={(value) => onFieldChange("OrgID", value)}
            />
            <GenericInput
              label="Jira Organization ID"
              value={partner.jiraOrganizationId ?? ""}
              onChange={(value) => onFieldChange("jiraOrganizationId", value)}
            />
            <GenericInput
              label="Tenant Type"
              value={partner.TenantType?.name ?? ""}
              readOnly
            />
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button
              className="px-4 py-2 border border-semantic-border-secondary rounded text-semantic-text-secondary hover:bg-semantic-state-active"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-semantic-bg-brand-primary text-white rounded hover:bg-semantic-bg-brand-primary-hover"
              onClick={onSave}
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-semantic-text-secondary">
          No partner data available
        </div>
      )}
    </GenericModal>
  );
};

